/**
 * Extract property keys that a calculation_dependencies blob depends on.
 * Catalog uses formula + inputs[]; legacy uses source_properties; some named
 * configs nest source_properties as string[] or {property_key} objects.
 */

export function dependencyKeysFromCalculationDependencies(
  calculationDependencies: Record<string, unknown> | null | undefined
): string[] {
  if (!calculationDependencies || typeof calculationDependencies !== 'object') {
    return [];
  }
  const found = new Set<string>();

  const addKey = (raw: unknown) => {
    if (typeof raw === 'string' && raw.trim()) {
      found.add(raw.trim());
      return;
    }
    if (raw && typeof raw === 'object') {
      const obj = raw as Record<string, unknown>;
      if (typeof obj.property_key === 'string' && obj.property_key.trim()) {
        found.add(obj.property_key.trim());
      } else if (typeof obj.name === 'string' && obj.name.trim()) {
        found.add(obj.name.trim());
      }
    }
  };

  const collectSourceProperties = (sourceProps: unknown) => {
    if (!sourceProps) return;
    const list = Array.isArray(sourceProps)
      ? sourceProps
      : typeof sourceProps === 'object'
        ? Object.values(sourceProps as Record<string, unknown>)
        : [];
    for (const dep of list) {
      if (typeof dep === 'string') {
        addKey(dep);
        continue;
      }
      if (!dep || typeof dep !== 'object') continue;
      const d = dep as Record<string, unknown>;
      if (typeof d.reference === 'string' && typeof d.property_index === 'number') {
        const refConfig = calculationDependencies[d.reference];
        if (refConfig && typeof refConfig === 'object') {
          const cfg = refConfig as Record<string, unknown>;
          const keys = Array.isArray(cfg.property_keys)
            ? cfg.property_keys
            : typeof cfg.property_key === 'string'
              ? [cfg.property_key]
              : [];
          addKey(keys[d.property_index]);
        }
      }
      addKey(d.property_key);
    }
  };

  collectSourceProperties(calculationDependencies.source_properties);

  const inputs = calculationDependencies.inputs;
  if (Array.isArray(inputs)) {
    for (const inp of inputs) addKey(inp);
  }

  for (const [key, value] of Object.entries(calculationDependencies)) {
    if (key === 'source_properties' || key === 'inputs' || key === 'formula') continue;
    if (!value || typeof value !== 'object' || Array.isArray(value)) continue;
    const nested = value as Record<string, unknown>;
    if ('source_properties' in nested) {
      collectSourceProperties(nested.source_properties);
    }
  }

  return [...found];
}
